'use client';

import { type Column, flexRender, type Row, type RowData, type Table as TableInstance } from '@tanstack/react-table';
import { Checkbox } from '@zealot/ui/components/checkbox';
import { DataTablePagination } from '@zealot/ui/components/data-table-pagination';
import { DataTableToolbar } from '@zealot/ui/components/data-table-toolbar';
import { Skeleton } from '@zealot/ui/components/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@zealot/ui/components/table';
import { type UseDataTableProps, useDataTable } from '@zealot/ui/hooks/useDataTable';
import { cn } from '@zealot/ui/lib/utils';
import { AlertCircle, Inbox } from 'lucide-react';
import type * as React from 'react';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    headerClassName?: string;
    cellClassName?: string;
    isFlexible?: boolean;
  }
}

type DataTableProps<TData> = UseDataTableProps<TData> & {
  isLoading?: boolean;
  isError?: boolean;
  loadingRows?: number;
  emptyMessage?: string | React.ReactNode;
  errorMessage?: string | React.ReactNode;
  showPagination?: boolean;
  showSelectedCount?: boolean;
  showToolbar?: boolean;
  pageSizeOptions?: number[];
  filterDebounceMs?: number;
  onRowClick?: (row: Row<TData>) => void;
  toolbar?: React.ReactNode | ((table: TableInstance<TData>) => React.ReactNode);
  footer?: React.ReactNode;
  isSelectionDisabled?: boolean;
  enableSelectAll?: boolean;
  getSelectionLabel?: (row: Row<TData>, isSelected: boolean) => string;
  getRowProps?: (row: Row<TData>) => React.ComponentProps<'tr'>;
  className?: string;
};

const CLICKABLE_ROW_EDGE = 'group-hover:shadow-[inset_4px_0_0_var(--color-primary)]';

const SKELETON_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-1/3'];
const SELECT_COLUMN_PX = 40;
const DEFAULT_COLUMN_SIZE = 150;
const DEFAULT_COLUMN_MIN_SIZE = 20;

const PINNED_HEAD = 'z-10 bg-muted';
const PINNED_CELL = cn(
  'z-10 bg-background',
  'group-hover:bg-[color-mix(in_oklab,var(--color-primary)_5%,var(--color-background))]',
  'group-data-[state=selected]:bg-[color-mix(in_oklab,var(--color-primary)_10%,var(--color-background))]',
);

const pinnedStyle = (left: number, width: number): React.CSSProperties => ({
  position: 'sticky',
  left,
  width,
});

// gloss
const explicitMinSize = <TData,>(column: Column<TData, unknown>) =>
  column.columnDef.minSize !== DEFAULT_COLUMN_MIN_SIZE ? column.columnDef.minSize : undefined;

// gloss
const DataTable = <TData,>({
  isLoading = false,
  isError = false,
  loadingRows = 5,
  emptyMessage = 'No results found.',
  errorMessage = 'Something went wrong.',
  showPagination = true,
  showSelectedCount,
  showToolbar = false,
  pageSizeOptions,
  filterDebounceMs,
  onRowClick,
  toolbar: toolbarSlot,
  footer,
  isSelectionDisabled = false,
  enableSelectAll = true,
  getSelectionLabel,
  getRowProps,
  className,
  ...hookProps
}: DataTableProps<TData>) => {
  const { table, globalFilter, setGlobalFilter } = useDataTable(hookProps);
  const { enableRowSelection } = hookProps;

  const resolvedToolbar = typeof toolbarSlot === 'function' ? toolbarSlot(table) : toolbarSlot;

  const columnCount = table.getVisibleLeafColumns().length + (enableRowSelection ? 1 : 0);
  const explicitWidth = (column: Column<TData, unknown>) =>
    column.columnDef.size !== DEFAULT_COLUMN_SIZE || table.getState().columnSizing[column.id] !== undefined
      ? column.getSize()
      : undefined;
  const hasPinned = table.getLeftVisibleLeafColumns().length > 0;
  const selectPx = enableRowSelection ? SELECT_COLUMN_PX : 0;
  const pinnedLeft = (column: Column<TData, unknown>) => selectPx + column.getStart('left');
  const visibleColumns = table.getVisibleLeafColumns();
  const hasFlexibleColumn = visibleColumns.some((column) => column.columnDef.meta?.isFlexible);
  const lastUnpinnedId = visibleColumns.findLast((column) => !column.getIsPinned())?.id;
  const isFlexible = (column: Column<TData, unknown>) =>
    hasFlexibleColumn ? !!column.columnDef.meta?.isFlexible : column.id === lastUnpinnedId;
  const flexibleColumns = visibleColumns.filter(isFlexible);
  const flexibleMin = (column: Column<TData, unknown>) =>
    hasFlexibleColumn ? (column.columnDef.minSize ?? 0) : column.getSize();
  const widestFlexibleMin = Math.max(0, ...flexibleColumns.map(flexibleMin));
  const tableMinWidth = hasPinned
    ? selectPx +
      visibleColumns.filter((column) => !isFlexible(column)).reduce((sum, column) => sum + column.getSize(), 0) +
      flexibleColumns.length * widestFlexibleMin
    : undefined;
  const columnStyle = (column: Column<TData, unknown>): React.CSSProperties => {
    if (column.getIsPinned()) return pinnedStyle(pinnedLeft(column), column.getSize());
    if (hasPinned) return isFlexible(column) ? {} : { width: column.getSize() };
    return { width: explicitWidth(column), minWidth: explicitMinSize(column) };
  };

  return (
    <div data-slot="data-table" className={cn('w-full min-w-0', className)}>
      {showToolbar && !resolvedToolbar && (
        <DataTableToolbar
          table={table}
          globalFilter={globalFilter}
          onGlobalFilterChange={setGlobalFilter}
          filterDebounceMs={filterDebounceMs}
        />
      )}
      {resolvedToolbar}

      <div className="rounded-lg border border-border">
        <Table
          className={cn(hasPinned && 'table-fixed')}
          style={{
            minWidth: tableMinWidth,
            width: hasPinned && flexibleColumns.length === 0 ? tableMinWidth : undefined,
          }}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {enableRowSelection && (
                  <TableHead
                    className={cn('w-10', hasPinned && PINNED_HEAD)}
                    style={hasPinned ? pinnedStyle(0, SELECT_COLUMN_PX) : undefined}
                  >
                    {enableSelectAll ? (
                      <Checkbox
                        checked={table.getIsAllPageRowsSelected()}
                        indeterminate={table.getIsSomePageRowsSelected()}
                        onCheckedChange={(checked) => table.toggleAllPageRowsSelected(!!checked)}
                        disabled={isSelectionDisabled}
                        aria-label="Select all"
                        size="sm"
                      />
                    ) : (
                      <span className="sr-only">Selected</span>
                    )}
                  </TableHead>
                )}
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(
                      header.column.columnDef.meta?.headerClassName,
                      header.column.getIsPinned() && PINNED_HEAD,
                    )}
                    style={{ position: 'relative', ...columnStyle(header.column) }}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {hookProps.enableColumnResizing && !(hasPinned && isFlexible(header.column)) && (
                      <div
                        role="none"
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          'absolute top-0 right-0 h-full w-1 cursor-col-resize touch-none select-none bg-border hover:bg-muted-foreground',
                          header.column.getIsResizing() && 'bg-primary',
                        )}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <LoadingRows columnCount={columnCount} rowCount={loadingRows} />
            ) : isError ? (
              <ErrorRow columnCount={columnCount} message={errorMessage} />
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => {
                const isSelected = row.getIsSelected();
                const rowProps = getRowProps?.(row);
                return (
                  <TableRow
                    key={row.id}
                    {...rowProps}
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={(event) => {
                      rowProps?.onClick?.(event);
                      onRowClick?.(row);
                    }}
                    className={cn('group', onRowClick && 'cursor-pointer', rowProps?.className)}
                  >
                    {enableRowSelection && (
                      <TableCell
                        className={cn(hasPinned && PINNED_CELL, onRowClick && CLICKABLE_ROW_EDGE)}
                        style={hasPinned ? pinnedStyle(0, SELECT_COLUMN_PX) : undefined}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => row.toggleSelected(!!checked)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={isSelectionDisabled}
                          aria-label={getSelectionLabel?.(row, isSelected) ?? 'Select row'}
                          size="sm"
                        />
                      </TableCell>
                    )}
                    {row.getVisibleCells().map((cell, cellIndex) => (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          cell.column.columnDef.meta?.cellClassName,
                          onRowClick && !enableRowSelection && cellIndex === 0 && CLICKABLE_ROW_EDGE,
                          cell.column.getIsPinned() ? PINNED_CELL : hasPinned && 'truncate',
                        )}
                        style={
                          cell.column.getIsPinned()
                            ? pinnedStyle(pinnedLeft(cell.column), cell.column.getSize())
                            : undefined
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : emptyMessage === null ? null : (
              <EmptyRow columnCount={columnCount} message={emptyMessage} />
            )}
            {footer && !isLoading && !isError && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columnCount}>{footer}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {showPagination && (
        <DataTablePagination
          table={table}
          pageSizeOptions={pageSizeOptions}
          showSelectedCount={showSelectedCount ?? enableRowSelection}
        />
      )}
    </div>
  );
};

const LoadingRows = ({ columnCount, rowCount }: { columnCount: number; rowCount: number }) => (
  <>
    {Array.from({ length: rowCount }).map((_, i) => (
      // biome-ignore lint/suspicious/noArrayIndexKey: stable loading skeleton rows
      <TableRow key={i} className="hover:bg-transparent">
        {Array.from({ length: columnCount }).map((_, j) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: stable loading skeleton cells
          <TableCell key={j}>
            <Skeleton className={cn('h-4', SKELETON_WIDTHS[(i + j) % SKELETON_WIDTHS.length])} />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

const EmptyRow = ({ columnCount, message }: { columnCount: number; message: string | React.ReactNode }) => (
  <TableRow className="hover:bg-transparent">
    <TableCell colSpan={columnCount}>
      <div className="flex flex-col items-center justify-center gap-3 py-10">
        <div className="flex size-10 items-center justify-center rounded-full bg-muted">
          <Inbox className="size-5 text-muted-foreground" />
        </div>
        <div className="text-muted-foreground text-xs">{message}</div>
      </div>
    </TableCell>
  </TableRow>
);

const ErrorRow = ({ columnCount, message }: { columnCount: number; message: string | React.ReactNode }) => (
  <TableRow className="hover:bg-transparent">
    <TableCell colSpan={columnCount}>
      <div className="flex flex-col items-center justify-center gap-3 py-10">
        <div className="flex size-10 items-center justify-center rounded-full bg-destructive/10">
          <AlertCircle className="size-5 text-destructive" />
        </div>
        <div className="text-muted-foreground text-xs">{message}</div>
      </div>
    </TableCell>
  </TableRow>
);

export type { DataTableProps };
export { DataTable };
